import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { getApiUrl } from "../../config";

type MfaVerifyLoginProps = {
  onVerified: () => void;
  onCancel: () => void;
};

export function MfaVerifyLogin({ onVerified, onCancel }: MfaVerifyLoginProps) {
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!verificationCode || verificationCode.length < 6) {
      setError("Please enter a valid verification code");
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl("api/mfa/verify"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: verificationCode }),
        credentials: "include",
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Verification failed");
      }

      // On success - we don't need to parse the response again as it's already done in auth hook
      // Show success message
      toast({
        title: "Verification successful",
        description: "You have been securely logged in",
        variant: "default",
        duration: 2000,
      });
      
      // Keep the verifying state active to maintain the loading indicator
      // This prevents UI flashing during the transition
      
      // Don't set verifying to false yet - keep showing the loading state
      // setIsVerifying(false); 
      
      // Apply a short delay to allow the success toast to be visible
      setTimeout(() => {
        // Navigate in one clean step - avoids the login page flash
        window.location.replace("/");
        
        // We don't need to call onVerified() which would close the modal
        // and potentially cause the login screen to flash briefly
      }, 1200);
    } catch (err) {
      setAttempts(prev => prev + 1);
      
      const errorMessage = err instanceof Error ? err.message : "Verification failed";
      setError(errorMessage);
      
      toast({
        title: "Verification failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <Card className="w-full max-w-md shadow-lg">
      <CardHeader>
        <CardTitle>Two-Factor Authentication</CardTitle>
        <CardDescription>
          Enter the 6-digit code from your authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {attempts >= 3 && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Multiple failed attempts</AlertTitle>
            <AlertDescription>
              You can also use one of your backup codes if you can't access your authenticator app.
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="verification-code" className="block text-sm font-medium">
              Verification Code
            </label>
            <Input
              id="verification-code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              className="text-center text-lg tracking-widest"
              placeholder="000000"
              maxLength={6}
              autoComplete="off"
              autoFocus
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Enter the code from your authenticator app (Google Authenticator, Duo, etc.)
            </p>
          </div>

          <div className="flex flex-col space-y-2 pt-2">
            <Button 
              type="submit" 
              disabled={isVerifying || !verificationCode}
              className="w-full"
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Verify & Continue"
              )}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="w-full"
              disabled={isVerifying}
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex justify-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Lost your authenticator device? Use one of your backup codes.
        </p>
      </CardFooter>
    </Card>
  );
}